import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { memo, useCallback, useLayoutEffect, useRef, type ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { pageCount } from '../lib/decimal';
import { IconButton } from './Button';
import { cx } from './cx';
import { Skeleton } from './Feedback';
import styles from './Table.module.css';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Plain-text label for the stacked phone layout when `header` is interactive (e.g. a sort button). */
  label?: string;
  cell: (row: T) => ReactNode;
  align?: 'start' | 'end';
  /** In the stacked phone layout, the primary column is the card title (no label). */
  isPrimary?: boolean;
  className?: string;
  /** Position of this value in the stacked phone card (lower comes first). */
  phoneOrder?: number;
  /** This column is the active sort: its header carries aria-sort. */
  sort?: 'ascending' | 'descending';
}

/** Single-row selection: one native radio at the row end; all radios of a table share `name`. */
export interface TableSelection<T> {
  name: string;
  selectedKey: string | null;
  onSelect: (row: T) => void;
  /** Accessible name of a row's radio (e.g. «اختيار INV-00012»). */
  label: (row: T) => string;
  /** Header of the selection column (visually hidden). */
  header: string;
  /** data-testid of each radio. */
  testId?: string;
}

export interface DataTableProps<T> {
  /** Accessible name of the table. */
  caption: string;
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** Row click opens the row (the primary cell also holds a real link for keyboard users). */
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  /** Shown instead of the table body when there are no rows. */
  empty?: ReactNode;
  footer?: ReactNode;
  testId?: string;
  getRowClassName?: (row: T) => string | undefined;
  getRowTestId?: (row: T) => string | undefined;
  selection?: TableSelection<T>;
}

/** Clicks on these never open the row: links, buttons, the selection radio and its label. */
const INTERACTIVE = 'a, button, input, label, select, textarea, [role="button"]';

interface RowProps<T> {
  row: T;
  columns: Column<T>[];
  className: string | undefined;
  testId: string | undefined;
  /** Stable callbacks (the table keeps the latest handler), so memoized rows skip re-rendering. */
  onRowClick: ((row: T) => void) | null;
  selectName: string | null;
  selectLabel: string | undefined;
  selectTestId: string | undefined;
  isSelected: boolean;
  onSelect: (row: T) => void;
}

function RowView<T>({
  row,
  columns,
  className,
  testId,
  onRowClick,
  selectName,
  selectLabel,
  selectTestId,
  isSelected,
  onSelect,
}: RowProps<T>) {
  return (
    <tr
      className={cx(onRowClick && styles.clickable, className)}
      data-testid={testId}
      onClick={
        onRowClick
          ? (event) => {
              // Let real links, buttons, the radio and text selection behave normally.
              if ((event.target as HTMLElement).closest(INTERACTIVE)) return;
              if (window.getSelection()?.toString()) return;
              onRowClick(row);
            }
          : undefined
      }
    >
      {columns.map((column) => (
        <td
          key={column.key}
          className={cx(
            column.align === 'end' && styles.end,
            column.isPrimary && styles.primaryCell,
            column.className,
          )}
          style={column.phoneOrder ? { order: column.phoneOrder } : undefined}
        >
          {/* display:none beside a real header; read as the label on phones */}
          <span className={styles.cellLabel}>{column.label ?? column.header}</span>
          <span>{column.cell(row)}</span>
        </td>
      ))}
      {selectName ? (
        <td className={styles.selectCell}>
          <label className={styles.selectHit}>
            <input
              type="radio"
              name={selectName}
              className={styles.radio}
              checked={isSelected}
              onChange={() => onSelect(row)}
              aria-label={selectLabel}
              data-testid={selectTestId}
            />
          </label>
        </td>
      ) : null}
    </tr>
  );
}

/** Rows re-render only when their own data, class or selection changes (polling, selecting). */
const Row = memo(RowView) as typeof RowView;

/**
 * Semantic table with a sticky header and server pagination. On phones the rows turn into stacked
 * cards (each value keeps its column label, ordered by `phoneOrder`); wider content scrolls inside
 * the frame, never the page. Optional single-row selection with a radio at the row end.
 */
export function DataTable<T>({
  caption,
  columns,
  rows,
  getRowKey,
  onRowClick,
  isLoading,
  empty,
  footer,
  testId,
  getRowClassName,
  getRowTestId,
  selection,
}: DataTableProps<T>) {
  const latest = useRef({ onRowClick, onSelect: selection?.onSelect });
  useLayoutEffect(() => {
    latest.current = { onRowClick, onSelect: selection?.onSelect };
  });
  const handleRowClick = useCallback((row: T) => latest.current.onRowClick?.(row), []);
  const handleSelect = useCallback((row: T) => latest.current.onSelect?.(row), []);

  const showEmpty = !isLoading && rows.length === 0 && empty;
  return (
    <div className={styles.frame} data-testid={testId}>
      {showEmpty ? (
        empty
      ) : (
        <div className={styles.scroller}>
          <table className={styles.table} aria-busy={isLoading || undefined}>
            <caption className="visually-hidden">{caption}</caption>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={column.sort}
                    className={cx(column.align === 'end' && styles.end, column.className)}
                  >
                    {column.header}
                  </th>
                ))}
                {selection ? (
                  <th scope="col" className={styles.selectHeader}>
                    <span className="visually-hidden">{selection.header}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {isLoading && rows.length === 0
                ? Array.from({ length: 5 }, (_, index) => (
                    <tr key={`skeleton-${index}`} className={styles.skeletonRow}>
                      {columns.map((column) => (
                        <td key={column.key}>
                          <Skeleton width={column.isPrimary ? '70%' : '50%'} />
                        </td>
                      ))}
                      {selection ? <td /> : null}
                    </tr>
                  ))
                : rows.map((row) => {
                    const key = getRowKey(row);
                    return (
                      <Row
                        key={key}
                        row={row}
                        columns={columns}
                        className={getRowClassName?.(row)}
                        testId={getRowTestId?.(row)}
                        onRowClick={onRowClick ? handleRowClick : null}
                        selectName={selection ? selection.name : null}
                        selectLabel={selection?.label(row)}
                        selectTestId={selection?.testId}
                        isSelected={selection ? selection.selectedKey === key : false}
                        onSelect={handleSelect}
                      />
                    );
                  })}
            </tbody>
          </table>
        </div>
      )}
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </div>
  );
}

export const tableStyles = styles;

export interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  pageSizes?: number[];
  /** Adds first/last page buttons (lists that can be long). */
  showEnds?: boolean;
}

export function Pagination({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  pageSizes = [25, 50, 100],
  showEnds = false,
}: PaginationProps) {
  const { t, formatNumber } = useI18n();
  const pages = pageCount(total, limit);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <nav className={styles.pagination} aria-label={t.pagination.label} data-testid="pagination">
      <p className={styles.paginationInfo} aria-live="polite" data-testid="pagination-range">
        {t.pagination.range(
          formatNumber(String(from)),
          formatNumber(String(to)),
          formatNumber(String(total)),
        )}
      </p>
      <div className={styles.paginationControls}>
        {onLimitChange ? (
          <label className={styles.pageSize}>
            {t.pagination.pageSize}
            <select
              value={limit}
              data-testid="page-size"
              onChange={(event) => onLimitChange(Number(event.target.value))}
            >
              {pageSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {showEnds ? (
          <IconButton
            variant="secondary"
            size="sm"
            label={t.pagination.first}
            icon={<ChevronsLeft size={16} className="flip-rtl" />}
            isDisabled={page <= 1}
            onPress={() => onPageChange(1)}
          />
        ) : null}
        <IconButton
          variant="secondary"
          size="sm"
          label={t.pagination.previous}
          icon={<ChevronLeft size={16} className="flip-rtl" />}
          isDisabled={page <= 1}
          onPress={() => onPageChange(page - 1)}
        />
        <span className={styles.paginationInfo}>
          {t.pagination.page(formatNumber(String(page)), formatNumber(String(pages)))}
        </span>
        <IconButton
          variant="secondary"
          size="sm"
          label={t.pagination.next}
          icon={<ChevronRight size={16} className="flip-rtl" />}
          isDisabled={page >= pages}
          onPress={() => onPageChange(page + 1)}
        />
        {showEnds ? (
          <IconButton
            variant="secondary"
            size="sm"
            label={t.pagination.last}
            icon={<ChevronsRight size={16} className="flip-rtl" />}
            isDisabled={page >= pages}
            onPress={() => onPageChange(pages)}
          />
        ) : null}
      </div>
    </nav>
  );
}
